import { Descriptions } from "@/components/v2";
import { Popconfirm, Space, type DescriptionsProps } from "antd";
import { UseMutationResult } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { CompanyDetailValues } from "../CompanyDetail";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { Button, Card } from "@rumbledirect/ui";

interface CompanyDetailCardProps {
  company?: CompanyDetailValues;
  isLoading?: boolean;
  deleteMutation?: UseMutationResult<null, Error, void, unknown>;
}

export const CompanyDetailCard = ({
  company,
  isLoading,
  deleteMutation,
}: CompanyDetailCardProps) => {
  const formatAddress = (address?: any) => {
    if (!address || !address.street) return null;

    const line1 = [
      address.street,
      address.streetNumber,
      address.streetNumberAddition,
    ]
      .filter(Boolean)
      .join(" ");
    const line2 = [address.postalCode, address.city].filter(Boolean).join(" ");

    return (
      <div>
        <div>{line1}</div>
        {(line2?.trim?.() ?? "") !== "" ? <div>{line2}</div> : null}
      </div>
    );
  };

  const geoLink = (() => {
    const lat = company?.geographyPoint?.latitude;
    const lon = company?.geographyPoint?.longitude;
    if (
      lat === undefined ||
      lon === undefined ||
      lat === null ||
      lon === null
    ) {
      return null;
    }
    const href = `https://www.google.com/maps/search/?api=1&query=${lat},${lon}`;
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        Bekijk op kaart ({lat}, {lon})
      </a>
    );
  })();

  const items: DescriptionsProps["items"] = [
    { label: "Groepsnaam", children: company?.groupName ?? null },
    { label: "Naam", children: company?.name ?? null },
    { label: "Type", children: company?.type ?? null },
    { label: "E-mail", children: company?.email ?? null },
    { label: "Telefoon", children: company?.telephone ?? null },
    { label: "Adres", children: formatAddress(company?.address) },
    { label: "Postadres", children: formatAddress(company?.postalAddress) },
    { label: "Geo locatie", children: geoLink },
  ];

  return (
    <Card
      loading={isLoading}
      className="w-full"
      title="Bedrijf"
      extra={
        <Space>
          <Link to={`/companies/${company?.id}/edit`}>
            <Button icon={<EditOutlined />} />
          </Link>
          <Popconfirm
            title="Bedrijf verwijderen"
            description="Weet je zeker dat je dit bedrijf wilt verwijderen?"
            onConfirm={() => deleteMutation?.mutate()}
            okText="Ja"
            cancelText="Nee"
          >
            <Button
              loading={deleteMutation?.isPending}
              danger
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      }
    >
      <Descriptions items={items} />
    </Card>
  );
};
